import { declineCityName, declineName, parsePlaceholderCase, formatAirportName } from './declension'
import { buildTripsterExperienceUrl } from '@/lib/affiliates/utils/tripster'

// {[AIRPORT_TBS]} — фигурные скобки без текста. Формат {текст[КОД]} требует текст перед «[»,
// поэтому такая запись разбиралась как старый [КОД], а скобки оставались на странице:
// «Tbilisi International Airport ({ Tbilisi Airport })» в georgia-vs-turkey-vacation (аудит 2026-09-16).
// PHOTO_ не трогаем — фото-плейсхолдеры со скобками разбираются отдельно.
const BARE_BRACE_PLACEHOLDER = /\{(\[(?!PHOTO_)[A-Za-z0-9_-]+\])\}/g

// Кириллические двойники латиницы, попадающие в коды плейсхолдеров при наборе:
// [PLACE_RAFAILOVIСI_BEACH] и [PLACE_MURIСI_BEACH] содержат кириллическую «С» и из-за неё
// не разбирались — читатель видел на странице сырые «{Пляж Рафаиловичи[PLACE_RAFAILOVIСI_BEACH]}»
// на 4 постах (скан блога 2026-09-17).
const CYRILLIC_LOOKALIKES = { А: 'A', В: 'B', Е: 'E', К: 'K', М: 'M', Н: 'H', О: 'O', Р: 'P', С: 'C', Т: 'T', У: 'Y', Х: 'X' }
const PLACEHOLDER_CODE = '[A-Za-z0-9_\\-\u0410-\u042f\u0430-\u044f]+'

export function normalizePlaceholderCode(rawCode) {
  return rawCode
    .toUpperCase()
    .replace(/-/g, '_')
    .replace(/[\u0410-\u042f]/g, (ch) => CYRILLIC_LOOKALIKES[ch] ?? ch)
}

// Провайдеры, которых выключили в Sanity, пока их коды жили в текстах: карта gptData берёт
// только active === true, и код выходил на страницу как есть — [AFF_HOLAFLY], {Drimsim[AFF_DRIMSIM]}
// в 32 статьях (краул 29.09.2026). Имя без ссылки лучше сырого кода.
const INACTIVE_PROVIDER_NAMES = {
  HOLAFLY: 'Holafly',
  GOMOWORLD: 'GoMoWorld',
  MTEL: 'Mtel',
  DRIMSIM: 'Drimsim',
}
const UNRESOLVED_CURLY = new RegExp(`\\{([^\\[{}]+)\\[(${PLACEHOLDER_CODE})(?:\\\\?\\|[^\\]]+)?\\][\\]\\}]`, 'g')
const UNRESOLVED_AFF = /\[AFF_([A-Za-z0-9_]+)(?:\\?\|[^\]]+)?\]/g

function prettyProviderName(code) {
  const upper = String(code).toUpperCase()
  if (INACTIVE_PROVIDER_NAMES[upper]) return INACTIVE_PROVIDER_NAMES[upper]
  return upper.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')
}

/**
 * Последний проход: всё, что резолвер не узнал, показываем текстом, а не кодом.
 * {текст[КОД]} → «текст», голый [AFF_X] → имя провайдера. Вызывается после всех замен,
 * поэтому разрешимые плейсхолдеры сюда уже не доходят.
 */
export function softenUnresolvedPlaceholders(text) {
  if (typeof text !== 'string') return text
  // Инлайн-код из markdown пайплайна приезжает литералом: `ibuprofen`, `Rehydron` в pharmacies
  // (ревью 30.09.2026). В тексте статьи кода не бывает — обратные кавычки просто снимаем.
  let plain = text.includes('`') ? text.replace(/`([^`\n]{1,80})`/g, '$1') : text
  // То же с курсивом markdown: *Добар дан* в разговорнике рынков (9 мест, ревью 30.09.2026).
  // Только пара звёздочек вплотную к словам — одиночная сноска «*» остаётся.
  if (plain.includes('*')) plain = plain.replace(/(^|[\s(«"])\*(?!\s)([^*\n]{1,80}?)(?<!\s)\*(?=$|[\s.,;:!?)»"])/g, '$1$2')
  if (!plain.includes('[')) return plain
  return plain
    .replace(UNRESOLVED_CURLY, (match, display) => display.trim())
    .replace(UNRESOLVED_AFF, (match, code) => prettyProviderName(code))
}

/** То же для PortableText-блока: только текст спанов, разметка не трогается. */
function softenBlock(block) {
  if (!block || !Array.isArray(block.children)) return block
  let changed = false
  const children = block.children.map((child) => {
    if (child?._type !== 'span' || typeof child.text !== 'string') return child
    const text = softenUnresolvedPlaceholders(child.text)
    if (text === child.text) return child
    changed = true
    return { ...child, text }
  })
  return changed ? { ...block, children } : block
}

/**
 * Для постов без страны (isGlobal) карты плейсхолдеров не строятся, и резолвер не
 * запускается вовсе — коды {Airalo[AFF_AIRALO]} выходили сырыми. Здесь только смягчение.
 */
export function softenPlaceholdersDeep(obj) {
  if (typeof obj === 'string') return softenUnresolvedPlaceholders(obj)
  if (Array.isArray(obj)) return obj.map(softenPlaceholdersDeep)
  if (obj && typeof obj === 'object') {
    if (obj._type === 'simpleTable') return obj
    if (Array.isArray(obj.children)) return softenBlock(obj)
    const result = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) result[key] = softenPlaceholdersDeep(obj[key])
    }
    return result
  }
  return obj
}

export function unwrapBareBracePlaceholders(text) {
  return typeof text === 'string' ? text.replace(BARE_BRACE_PLACEHOLDER, '$1') : text
}

/**
 * Заменяет плейсхолдеры в тексте на актуальные названия
 * @param {string} text - Текст с плейсхолдерами
 * @param {Object} gptData - Объект с мапами плейсхолдеров из getCountryGptData
 * @returns {string} Текст с замененными плейсхолдерами
 */
export function replacePlaceholders(text, gptData) {
  if (!text || typeof text !== 'string') {
    return text
  }

  if (!gptData) {
    return text
  }

  const language = gptData._meta?.language || 'ru'
  const isRussian = language === 'ru'

  let result = unwrapBareBracePlaceholders(text)

  // Обрабатываем новый формат: {текст[PLACEHOLDER]} или {текст[PLACEHOLDER]]
  // Пример: {Дохе[CITY_DOHA]} -> "Дохе" (просто текст, без ссылки, т.к. это не PortableText)
  // Поддерживаем оба варианта закрытия: ]} и ]]
  // Хвост `|URL` (в контенте встречается и экранированным, `\\|`) здесь только отбрасывается:
  // ссылки строит replacePlaceholdersDeep, а в плоской строке нужен один текст.
  const curlyBracePlaceholderRegex = new RegExp(`\\{([^\\[]+)\\[(${PLACEHOLDER_CODE})(?:\\\\?\\|[^\\]]+)?\\][\\]\\}]`, 'g')
  result = result.replace(curlyBracePlaceholderRegex, (fullMatch, displayText, placeholderCode) => {
    // Нормализуем код: uppercase + подчёркивания (контент может содержать lowercase/дефисы)
    const placeholder = `[${normalizePlaceholderCode(placeholderCode)}]`
    
    // Проверяем, есть ли данные для этого плейсхолдера
    if (gptData.countries && gptData.countries[placeholder]) {
      return displayText
    } else if (gptData.cities && gptData.cities[placeholder]) {
      // Для городов просто возвращаем текст из фигурных скобок
      return displayText
    } else if (gptData.pois && gptData.pois[placeholder]) {
      // Для POI просто возвращаем текст из фигурных скобок
      return displayText
    } else if (gptData.airports && gptData.airports[placeholder]) {
      // Для аэропортов просто возвращаем текст из фигурных скобок
      return displayText
    } else if (gptData.providers && gptData.providers[placeholder]) {
      // Для провайдеров просто возвращаем текст из фигурных скобок
      return displayText
    } else if (gptData.services && gptData.services[placeholder]) {
      // Для сервисов просто возвращаем текст из фигурных скобок
      return displayText
    } else if (gptData.posts && gptData.posts[placeholder]) {
      // Для постов просто возвращаем текст из фигурных скобок
      return displayText
    } else if (placeholder.startsWith('[COUNTRY_')) {
      // Для стран возвращаем текст из фигурных скобок (название страны).
      // Ссылка на страновой хаб создаётся отдельно в processPortableTextBlock.
      return displayText
    } else if (placeholder.startsWith('[PAGE_')) {
      // Фоллбек для сервисов, не найденных в Sanity — используем текст из фигурных скобок
      return displayText
    }

    // Если плейсхолдер не найден, оставляем как есть
    return fullMatch
  })

  if (gptData.countries && typeof gptData.countries === 'object') {
    for (const [placeholder, data] of Object.entries(gptData.countries)) {
      const name = typeof data === 'string' ? data : data?.name
      if (name) result = result.replace(new RegExp(escapeRegex(placeholder), 'g'), name)
    }
  }

  // Заменяем плейсхолдеры городов (только текст, ссылки создаются в replacePlaceholdersDeep)
  if (gptData.cities && typeof gptData.cities === 'object') {
    // Сначала обрабатываем плейсхолдеры с падежными маркерами
    // Ищем все возможные варианты плейсхолдеров с падежами
    const caseSuffixes = ['_IN', '_FROM', '_TO', '_PREP', '_GEN', '_DAT', '_ACC', '_INS']

    for (const [basePlaceholder, data] of Object.entries(gptData.cities)) {
      const rawName = typeof data === 'string' ? data : data?.name
      const name = typeof rawName === 'string' ? rawName.trim() : rawName
      if (!name) continue

      // Заменяем базовый плейсхолдер (именительный падеж)
      result = result.replace(new RegExp(escapeRegex(basePlaceholder), 'g'), name)

      // Заменяем плейсхолдеры с падежными маркерами для русской версии
      if (isRussian) {
        for (const suffix of caseSuffixes) {
          const casePlaceholder = basePlaceholder.slice(0, -1) + suffix + ']'
          const { caseType } = parsePlaceholderCase(casePlaceholder)
          const declinedName = declineCityName(name, caseType)
          result = result.replace(new RegExp(escapeRegex(casePlaceholder), 'g'), declinedName)
        }
      }
    }
  }

  // Заменяем плейсхолдеры POI (только текст, ссылки создаются в replacePlaceholdersDeep)
  if (gptData.pois && typeof gptData.pois === 'object') {
    // Обрабатываем плейсхолдеры с падежными маркерами
    const caseSuffixes = ['_IN', '_FROM', '_TO', '_GEN', '_DAT', '_ACC', '_INS', '_PREP']

    for (const [basePlaceholder, data] of Object.entries(gptData.pois)) {
      const rawName = typeof data === 'string' ? data : data?.name
      const name = typeof rawName === 'string' ? rawName.trim() : rawName
      if (!name) continue

      // Заменяем базовый плейсхолдер (именительный падеж)
      result = result.replace(new RegExp(escapeRegex(basePlaceholder), 'g'), name)

      // Заменяем плейсхолдеры с падежными маркерами для русской версии
      if (isRussian) {
        for (const suffix of caseSuffixes) {
          const casePlaceholder = basePlaceholder.slice(0, -1) + suffix + ']'
          const { caseType } = parsePlaceholderCase(casePlaceholder)
          const declinedName = declineName(name, caseType)
          result = result.replace(new RegExp(escapeRegex(casePlaceholder), 'g'), declinedName)
        }
      }
    }
  }

  // Заменяем плейсхолдеры аэропортов
  // Формат: [AIRPORT_ZRH] -> "Аэропорт Цюриха" (ru) / "Zurich Airport" (en)
  // С падежами: [AIRPORT_ZRH_GEN] -> "Аэропорта Цюриха" и т.д.
  if (gptData.airports && typeof gptData.airports === 'object') {
    const caseSuffixes = ['_IN', '_FROM', '_TO', '_GEN', '_DAT', '_ACC', '_INS', '_PREP']

    for (const [basePlaceholder, data] of Object.entries(gptData.airports)) {
      if (!data) continue

      // Получаем название города для формирования "Аэропорт Цюриха"
      const rawCityName = typeof data === 'string' ? data : data?.cityName
      const cityName = typeof rawCityName === 'string' ? rawCityName.trim() : rawCityName
      if (!cityName) continue

      // Заменяем базовый плейсхолдер (именительный падеж)
      // Русский: "Аэропорт Цюриха", Английский: "Zurich Airport"
      const formattedName = formatAirportName(cityName, 'nominative', language)
      result = result.replace(new RegExp(escapeRegex(basePlaceholder), 'g'), formattedName)

      // Заменяем плейсхолдеры с падежными маркерами
      for (const suffix of caseSuffixes) {
        const casePlaceholder = basePlaceholder.slice(0, -1) + suffix + ']'
        const { caseType } = parsePlaceholderCase(casePlaceholder)
        // Формируем название с учетом падежа: "Аэропорта Цюриха", "в Аэропорте Цюриха" и т.д.
        const declinedName = formatAirportName(cityName, caseType, language)
        result = result.replace(new RegExp(escapeRegex(casePlaceholder), 'g'), declinedName)
      }
    }
  }

  // Заменяем плейсхолдеры сервисов (только текст, ссылки создаются в replacePlaceholdersDeep)
  if (gptData.services && typeof gptData.services === 'object') {
    for (const [placeholder, data] of Object.entries(gptData.services)) {
      const rawName = typeof data === 'string' ? data : data?.name
      const name = typeof rawName === 'string' ? rawName.trim() : rawName
      if (name) {
        result = result.replace(new RegExp(escapeRegex(placeholder), 'g'), name)
      }
    }
  }

  // Заменяем плейсхолдеры провайдеров (только текст, ссылки создаются в replacePlaceholdersDeep)
  if (gptData.providers && typeof gptData.providers === 'object') {
    for (const [placeholder, data] of Object.entries(gptData.providers)) {
      if (!data?.title) {
        if (process.env.NODE_ENV === 'development' && result.includes(placeholder)) {
          console.warn(`⚠️ Провайдер ${placeholder} имеет пустое имя (null/undefined)`)
        }
        continue
      }
      result = result.replace(new RegExp(escapeRegex(placeholder), 'g'), data.title)
    }
  }

  // Заменяем плейсхолдеры постов (только текст, ссылки создаются в replacePlaceholdersDeep)
  if (gptData.posts && typeof gptData.posts === 'object') {
    for (const [placeholder, data] of Object.entries(gptData.posts)) {
      const rawName = typeof data === 'string' ? data : data?.name
      const name = typeof rawName === 'string' ? rawName.trim() : rawName
      if (name) {
        result = result.replace(new RegExp(escapeRegex(placeholder), 'g'), name)
      }
    }
  }

  // Удаляем фото-плейсхолдеры из текста (фото обрабатываются на уровне блоков в replacePlaceholdersDeep)
  // Поддерживаем оба формата: {[PHOTO_xxx]} и [PHOTO_xxx]
  result = result.replace(/\{?\[PHOTO_[A-Za-z0-9_-]+\]\}?/g, '')

  // Логируем незамененные плейсхолдеры в режиме разработки
  if (process.env.NODE_ENV === 'development') {
    const unreplacedPlaceholders = result.match(/\[(CITY|PLACE|AIRPORT|PAGE|POST|AFF|PHOTO)_[A-Za-z0-9_-]+\]/g)
    if (unreplacedPlaceholders && unreplacedPlaceholders.length > 0) {
      const unique = [...new Set(unreplacedPlaceholders)]
      console.warn('⚠️ Незамененные плейсхолдеры:', unique.join(', '))
    }
  }

  return softenUnresolvedPlaceholders(result)
}

/**
 * Генерирует уникальный ключ для markDef
 */
function generateMarkKey() {
  return `mark-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Проверяет, является ли блок PortableText фото-плейсхолдером {[PHOTO_xxx]}
 * @param {Object} block - Блок PortableText
 * @param {Object} gptData - Данные для замены плейсхолдеров
 * @returns {Object|null} Данные фото { photoAsset: {...} } или null
 */
function getPhotoPlaceholderFromBlock(block, gptData) {
  if (!block?.children || !gptData?.photos) return null
  if (block.children.length !== 1) return null
  const child = block.children[0]
  if (child._type !== 'span' || !child.text) return null

  const text = child.text.trim()
  // Поддерживаем оба формата: {[PHOTO_xxx]} и [PHOTO_xxx]
  const match = text.match(/^\{?\[PHOTO_([A-Za-z0-9_-]+)\]\}?$/)
  if (!match) return null

  const placeholder = `[PHOTO_${match[1]}]`
  return gptData.photos[placeholder] || null
}

/**
 * Обрабатывает блок PortableText, заменяя все плейсхолдеры на ссылки
 * @param {Object} block - Блок PortableText
 * @param {Object} gptData - Данные для замены плейсхолдеров
 * @returns {Object} Обработанный блок с markDefs для ссылок
 */
function processPortableTextBlock(block, gptData) {
  if (!block || !gptData) {
    return block
  }

  // Обрабатываем только блоки с children (текстовые блоки)
  if (!block.children || !Array.isArray(block.children)) {
    return block
  }

  const processedChildren = []
  const markDefs = block.markDefs ? [...block.markDefs] : []
  const language = gptData._meta?.language || 'ru'
  const countrySlug = gptData._meta?.countrySlug

  // Собираем все плейсхолдеры всех типов
  const allPlaceholders = {
    countries: gptData.countries || {},
    cities: gptData.cities || {},
    pois: gptData.pois || {},
    airports: gptData.airports || {},
    services: gptData.services || {},
    posts: gptData.posts || {},
    providers: gptData.providers || {},
    countries: gptData.countries || {}
  }

  // Логирование загруженных плейсхолдеров убрано для уменьшения шума в консоли

  // Обрабатываем каждый child (span)
  block.children.forEach(child => {
    if (child._type !== 'span' || !child.text) {
      // Рекурсивно обрабатываем другие типы children
      processedChildren.push(replacePlaceholdersDeep(child, gptData))
      return
    }

    const text = unwrapBareBracePlaceholders(child.text)
    const existingMarks = child.marks || []

    // Находим все плейсхолдеры в тексте
    const matches = []

    // Обрабатываем новый формат: {текст[PLACEHOLDER]} или {текст[PLACEHOLDER]]
    // Расширенный формат с deep link: {текст[PLACEHOLDER|URL]}
    // Пример: {Дохе[CITY_DOHA]} -> "Дохе" (кликабельная ссылка на город)
    // Пример: {25,2 $[AFF_YESIM|https://yesim.app/...]} -> "25,2 $" (ссылка на тариф)
    // Поддерживаем оба варианта закрытия: ]} и ]]
    // \\| — экранированная черта из markdown-таблиц: {15 €[AFF_VOYE\\|https://...]} в esim-vs-local-sim-montenegro
    const curlyBracePlaceholderRegex = new RegExp(`\\{([^\\[]+)\\[(${PLACEHOLDER_CODE})(?:\\\\?\\|([^\\]]+))?\\][\\]\\}]`, 'g')
    const curlyBraceRanges = [] // Сохраняем диапазоны новых форматов, чтобы исключить обработку старых внутри них

    let curlyMatch
    while ((curlyMatch = curlyBracePlaceholderRegex.exec(text)) !== null) {
      const fullMatch = curlyMatch[0] // Полное совпадение: {Дохе[CITY_DOHA]} или {25,2 $[AFF_YESIM|URL]}
      const displayText = curlyMatch[1] // Текст для отображения: "Дохе" или "25,2 $"
      // Нормализуем код: uppercase + подчёркивания (контент может содержать lowercase/дефисы)
      const rawCode = curlyMatch[2]
      const normalizedCode = normalizePlaceholderCode(rawCode)
      const placeholder = `[${normalizedCode}]` // Плейсхолдер: [CITY_DOHA]
      const deepLink = curlyMatch[3] || null // URL после |, если указан
      
      // Сохраняем диапазон для исключения обработки старых плейсхолдеров внутри
      curlyBraceRanges.push({
        start: curlyMatch.index,
        end: curlyMatch.index + fullMatch.length
      })
      
      // Определяем тип плейсхолдера и находим данные
      let matchType = null
      let matchData = null
      
      if (placeholder.startsWith('[COUNTRY_')) {
        if (allPlaceholders.countries[placeholder]) {
          matchType = 'country'
          matchData = allPlaceholders.countries[placeholder]
        }
      } else if (placeholder.startsWith('[CITY_')) {
        // Ищем базовый плейсхолдер города (без падежных суффиксов)
        const basePlaceholder = placeholder
        if (allPlaceholders.cities[basePlaceholder]) {
          matchType = 'city'
          matchData = allPlaceholders.cities[basePlaceholder]
        }
      } else if (placeholder.startsWith('[PLACE_')) {
        // Ищем базовый плейсхолдер POI (без падежных суффиксов)
        const basePlaceholder = placeholder
        if (allPlaceholders.pois[basePlaceholder]) {
          matchType = 'poi'
          matchData = allPlaceholders.pois[basePlaceholder]
        }
      } else if (placeholder.startsWith('[AIRPORT_')) {
        // Ищем базовый плейсхолдер аэропорта (без падежных суффиксов)
        const basePlaceholder = placeholder
        if (allPlaceholders.airports[basePlaceholder]) {
          matchType = 'airport'
          matchData = allPlaceholders.airports[basePlaceholder]
        }
      } else if (placeholder.startsWith('[AFF_')) {
        // Ищем плейсхолдер провайдера
        const basePlaceholder = placeholder
        if (allPlaceholders.providers[basePlaceholder]) {
          matchType = 'provider'
          matchData = allPlaceholders.providers[basePlaceholder]
        } else if (normalizedCode === 'AFF_TRIPSTER' && deepLink && /^\d+$/.test(deepLink)) {
          // AFF_TRIPSTER с числовым ID -> строим URL экскурсии и создаём обычную ссылку
          matchType = 'tripsterLink'
          matchData = { href: buildTripsterExperienceUrl(deepLink) }
        }
      } else if (placeholder.startsWith('[PAGE_')) {
        // Ищем плейсхолдер сервиса
        const basePlaceholder = placeholder
        if (allPlaceholders.services[basePlaceholder]) {
          matchType = 'service'
          matchData = allPlaceholders.services[basePlaceholder]
        } else if (countrySlug) {
          // Фоллбек: парсим плейсхолдер [PAGE_GEORGIA_TRANSPORT] → country=georgia, code=transport
          const inner = placeholder.slice(6, -1) // Убираем [PAGE_ и ]
          const countryUpper = countrySlug.toUpperCase()
          if (inner.startsWith(countryUpper + '_')) {
            const serviceCode = inner.slice(countryUpper.length + 1).toLowerCase().replace(/_/g, '-')
            matchType = 'service'
            matchData = {
              name: displayText || serviceCode,
              service: {
                _id: `page-${countrySlug}-${serviceCode}`,
                code: serviceCode,
                name: displayText || serviceCode
              }
            }
          }
        }
      } else if (placeholder.startsWith('[POST_')) {
        // Ищем плейсхолдер поста
        const basePlaceholder = placeholder
        if (allPlaceholders.posts[basePlaceholder]) {
          matchType = 'post'
          matchData = allPlaceholders.posts[basePlaceholder]
        }
      } else if (placeholder.startsWith('[COUNTRY_')) {
        // Страна: [COUNTRY_QATAR] -> slug "qatar" (ссылка на страновой хаб /{lang}/{slug}).
        // Название страны берём из текста в фигурных скобках, поэтому displayText всегда
        // рендерится, даже если страна отсутствует в карте (тогда ссылка не создаётся).
        matchType = 'country'
        const inner = placeholder.slice(9, -1) // убираем "[COUNTRY_" и "]"
        const slug = inner.toLowerCase().replace(/_/g, '-')
        matchData = {
          slug,
          exists: Boolean(allPlaceholders.countries[placeholder])
        }
      }
      
      if (matchType && matchData) {
        matches.push({
          placeholder: fullMatch,
          index: curlyMatch.index,
          length: fullMatch.length,
          type: matchType,
          data: matchData,
          displayText: displayText, // Используем текст из фигурных скобок
          deepLink: deepLink, // URL из формата {текст[КОД|URL]}, если указан
          isCurlyBraceFormat: true
        })
      }
    }
    
    // Функция для проверки, находится ли позиция внутри нового формата
    const isInsideCurlyBrace = (index, length) => {
      return curlyBraceRanges.some(range => {
        // Проверяем, что плейсхолдер полностью находится внутри диапазона нового формата
        const isInside = index >= range.start && (index + length) <= range.end
        return isInside
      })
    }

    // Провайдеры
    Object.keys(allPlaceholders.providers).forEach(placeholder => {
      if (text.includes(placeholder)) {
        const regex = new RegExp(escapeRegex(placeholder), 'g')
        let match
        while ((match = regex.exec(text)) !== null) {
          // Пропускаем, если плейсхолдер находится внутри нового формата
          if (isInsideCurlyBrace(match.index, placeholder.length)) {
            continue
          }
          matches.push({
            placeholder,
            index: match.index,
            length: placeholder.length,
            type: 'provider',
            data: allPlaceholders.providers[placeholder]
          })
        }
      }
    })

    // Города (включая варианты с падежными маркерами)
    Object.keys(allPlaceholders.cities).forEach(basePlaceholder => {
      // Проверяем базовый плейсхолдер и варианты с падежами
      const caseSuffixes = ['', '_IN', '_FROM', '_TO', '_PREP', '_GEN', '_DAT', '_ACC', '_INS']
      const data = allPlaceholders.cities[basePlaceholder]

      caseSuffixes.forEach(suffix => {
        const placeholder = suffix
          ? basePlaceholder.slice(0, -1) + suffix + ']'
          : basePlaceholder

        if (text.includes(placeholder)) {
          const regex = new RegExp(escapeRegex(placeholder), 'g')
          let match
          while ((match = regex.exec(text)) !== null) {
            // Пропускаем, если плейсхолдер находится внутри нового формата
            if (isInsideCurlyBrace(match.index, placeholder.length)) {
              continue
            }
            matches.push({
              placeholder,
              index: match.index,
              length: placeholder.length,
              type: 'city',
              data: data
            })
          }
        }
      })
    })

    // POI (включая варианты с падежными маркерами)
    Object.keys(allPlaceholders.pois).forEach(basePlaceholder => {
      // Проверяем базовый плейсхолдер и варианты с падежами
      const caseSuffixes = ['', '_IN', '_FROM', '_TO', '_GEN', '_DAT', '_ACC', '_INS', '_PREP']
      const data = allPlaceholders.pois[basePlaceholder]

      caseSuffixes.forEach(suffix => {
        const placeholder = suffix
          ? basePlaceholder.slice(0, -1) + suffix + ']'
          : basePlaceholder

        if (text.includes(placeholder)) {
          const regex = new RegExp(escapeRegex(placeholder), 'g')
          let match
          while ((match = regex.exec(text)) !== null) {
            // Пропускаем, если плейсхолдер находится внутри нового формата
            if (isInsideCurlyBrace(match.index, placeholder.length)) {
              continue
            }
            matches.push({
              placeholder,
              index: match.index,
              length: placeholder.length,
              type: 'poi',
              data: data
            })
          }
        }
      })
    })

    // Аэропорты (включая варианты с падежными маркерами)
    Object.keys(allPlaceholders.airports).forEach(basePlaceholder => {
      const caseSuffixes = ['', '_IN', '_FROM', '_TO', '_GEN', '_DAT', '_ACC', '_INS', '_PREP']
      const data = allPlaceholders.airports[basePlaceholder]

      caseSuffixes.forEach(suffix => {
        const placeholder = suffix
          ? basePlaceholder.slice(0, -1) + suffix + ']'
          : basePlaceholder

        if (text.includes(placeholder)) {
          const regex = new RegExp(escapeRegex(placeholder), 'g')
          let match
          while ((match = regex.exec(text)) !== null) {
            // Пропускаем, если плейсхолдер находится внутри нового формата
            if (isInsideCurlyBrace(match.index, placeholder.length)) {
              continue
            }
            matches.push({
              placeholder,
              index: match.index,
              length: placeholder.length,
              type: 'airport',
              data: data
            })
          }
        }
      })
    })

    // Сервисы
    Object.keys(allPlaceholders.services).forEach(placeholder => {
      if (text.includes(placeholder)) {
        const regex = new RegExp(escapeRegex(placeholder), 'g')
        let match
        while ((match = regex.exec(text)) !== null) {
          // Пропускаем, если плейсхолдер находится внутри нового формата
          if (isInsideCurlyBrace(match.index, placeholder.length)) {
            continue
          }
          matches.push({
            placeholder,
            index: match.index,
            length: placeholder.length,
            type: 'service',
            data: allPlaceholders.services[placeholder]
          })
        }
      }
    })

    // Посты
    Object.keys(allPlaceholders.posts).forEach(placeholder => {
      if (text.includes(placeholder)) {
        const regex = new RegExp(escapeRegex(placeholder), 'g')
        let match
        while ((match = regex.exec(text)) !== null) {
          // Пропускаем, если плейсхолдер находится внутри нового формата
          if (isInsideCurlyBrace(match.index, placeholder.length)) {
            continue
          }
          matches.push({
            placeholder,
            index: match.index,
            length: placeholder.length,
            type: 'post',
            data: allPlaceholders.posts[placeholder]
          })
        }
      }
    })

    // Если нет плейсхолдеров, просто заменяем текст
    if (matches.length === 0) {
      const replacedText = replacePlaceholders(text, gptData)
      if (replacedText !== text) {
        processedChildren.push({
          ...child,
          text: replacedText
        })
      } else {
        processedChildren.push(child)
      }
      return
    }

    // Сортируем по позиции
    matches.sort((a, b) => a.index - b.index)

    let lastIndex = 0

    // Разбиваем текст на части
    matches.forEach(match => {
      // Текст до плейсхолдера
      if (match.index > lastIndex) {
        const beforeText = text.substring(lastIndex, match.index)
        if (beforeText) {
          const replacedBefore = replacePlaceholders(beforeText, gptData)
          if (replacedBefore) {
            processedChildren.push({
              ...child,
              _key: child._key || generateMarkKey(),
              text: replacedBefore,
              marks: existingMarks
            })
          }
        }
      }

      // Создаем markDef и span в зависимости от типа
      let markKey = null
      let displayText = null

      // Обработка нового формата {текст[PLACEHOLDER]}
      if (match.isCurlyBraceFormat && match.displayText) {
        // Для нового формата используем текст из фигурных скобок
        displayText = match.displayText
        
        if (match.type === 'city' && match.data?.city && countrySlug) {
          const existingMarkDef = markDefs.find(md =>
            md._type === 'cityLink' && md.reference?._id === match.data.city._id
          )
          if (existingMarkDef) {
            markKey = existingMarkDef._key
          } else {
            markKey = generateMarkKey()
            markDefs.push({
              _key: markKey,
              _type: 'cityLink',
              reference: {
                _id: match.data.city._id,
                slug: { current: match.data.city.slug },
                name: match.data.city.name,
                countrySlug: match.data.city.countrySlug || countrySlug,
                country: {
                  slug: { current: match.data.city.countrySlug || countrySlug }
                }
              }
            })
          }
        } else if (match.type === 'poi' && match.data?.poi) {
          const existingMarkDef = markDefs.find(md =>
            md._type === 'poiLink' && md.reference?._id === match.data.poi._id
          )
          if (existingMarkDef) {
            markKey = existingMarkDef._key
          } else {
            markKey = generateMarkKey()
            markDefs.push({
              _key: markKey,
              _type: 'poiLink',
              reference: {
                _id: match.data.poi._id,
                slug: { current: match.data.poi.slug },
                nameEn: match.data.poi.nameEn,
                countrySlug: match.data.poi.countrySlug,
                citySlug: match.data.poi.citySlug,
                city: {
                  slug: { current: match.data.poi.citySlug }
                }
              }
            })
          }
        } else if (match.type === 'airport' && match.data?.airport && match.data?.cityName && countrySlug) {
          const existingMarkDef = markDefs.find(md =>
            md._type === 'airportLink' && md.reference?.iata === match.data.airport.iata
          )
          if (existingMarkDef) {
            markKey = existingMarkDef._key
          } else {
            markKey = generateMarkKey()
            markDefs.push({
              _key: markKey,
              _type: 'airportLink',
              reference: {
                iata: match.data.airport.iata,
                slug: match.data.airport.slug,
                countrySlug: match.data.airport.countrySlug || countrySlug
              }
            })
          }
        } else if (match.type === 'provider' && match.data?.provider && match.data?.title) {
          // Для провайдеров используем текст из фигурных скобок
          // При наличии deepLink — учитываем его при поиске существующего markDef
          const matchDeepLink = match.deepLink || null
          const existingMarkDef = markDefs.find(md =>
            md._type === 'providerLink' && md.reference?._id === match.data.provider._id && md.reference?.deepLink === matchDeepLink
          )
          if (existingMarkDef) {
            markKey = existingMarkDef._key
          } else {
            markKey = generateMarkKey()
            markDefs.push({
              _key: markKey,
              _type: 'providerLink',
              reference: {
                _id: match.data.provider._id,
                code: match.data.provider.code,
                title: match.data.provider.title,
                vertical: match.data.provider.vertical,
                trackingBaseUrl: match.data.provider.trackingBaseUrl,
                redirectBaseUrl: match.data.provider.redirectBaseUrl,
                linkMode: match.data.provider.linkMode,
                subIdParam: match.data.provider.subIdParam,
                defaultParams: match.data.provider.defaultParams,
                paramMap: match.data.provider.paramMap,
                linkTemplates: match.data.provider.linkTemplates,
                mainPageUrl: match.data.provider.mainPageUrl,
                deepLink: matchDeepLink
              }
            })
          }
        } else if (match.type === 'service' && match.data?.service && match.data?.name && countrySlug) {
          // Для сервисов используем текст из фигурных скобок
          const existingMarkDef = markDefs.find(md =>
            md._type === 'serviceLink' && md.reference?._id === match.data.service._id
          )
          if (existingMarkDef) {
            markKey = existingMarkDef._key
          } else {
            markKey = generateMarkKey()
            markDefs.push({
              _key: markKey,
              _type: 'serviceLink',
              reference: {
                _id: match.data.service._id,
                code: match.data.service.code,
                name: match.data.service.name
              }
            })
          }
        } else if (match.type === 'post' && match.data?.post) {
          // Для постов используем текст из фигурных скобок
          const existingMarkDef = markDefs.find(md =>
            md._type === 'internalLink' && md.reference?._id === match.data.post._id
          )
          if (existingMarkDef) {
            markKey = existingMarkDef._key
          } else {
            markKey = generateMarkKey()
            markDefs.push({
              _key: markKey,
              _type: 'internalLink',
              reference: {
                _id: match.data.post._id,
                slug: { current: match.data.post.slug },
                titleRu: match.data.post.titleRu,
                titleEn: match.data.post.titleEn
              }
            })
          }
        } else if (match.type === 'country' && match.data?.slug && match.data?.exists) {
          // Страна: ссылка на страновой хаб /{lang}/{slug}. displayText уже задан выше.
          const existingMarkDef = markDefs.find(md =>
            md._type === 'countryLink' && md.reference?.slug === match.data.slug
          )
          if (existingMarkDef) {
            markKey = existingMarkDef._key
          } else {
            markKey = generateMarkKey()
            markDefs.push({
              _key: markKey,
              _type: 'countryLink',
              reference: {
                slug: match.data.slug
              }
            })
          }
        } else if (match.type === 'tripsterLink' && match.data?.href) {
          // AFF_TRIPSTER: создаём обычную ссылку — marks.link в portableTextConfig добавит партнёрские параметры
          const tripsterHref = match.data.href
          const existingMarkDef = markDefs.find(md =>
            md._type === 'link' && md.href === tripsterHref
          )
          if (existingMarkDef) {
            markKey = existingMarkDef._key
          } else {
            markKey = generateMarkKey()
            markDefs.push({
              _key: markKey,
              _type: 'link',
              href: tripsterHref
            })
          }
        }
      } else if (match.type === 'provider' && match.data?.provider && match.data?.title) {
        displayText = match.data.title
        const existingMarkDef = markDefs.find(md =>
          md._type === 'providerLink' && md.reference?._id === match.data.provider._id
        )
        if (existingMarkDef) {
          markKey = existingMarkDef._key
        } else {
          markKey = generateMarkKey()
          markDefs.push({
            _key: markKey,
            _type: 'providerLink',
            reference: {
              _id: match.data.provider._id,
              code: match.data.provider.code,
              title: match.data.provider.title,
              vertical: match.data.provider.vertical,
              trackingBaseUrl: match.data.provider.trackingBaseUrl,
              redirectBaseUrl: match.data.provider.redirectBaseUrl,
              linkMode: match.data.provider.linkMode,
              subIdParam: match.data.provider.subIdParam,
              defaultParams: match.data.provider.defaultParams,
              paramMap: match.data.provider.paramMap,
              linkTemplates: match.data.provider.linkTemplates,
              mainPageUrl: match.data.provider.mainPageUrl
            }
          })
        }
      } else if (match.type === 'city' && match.data?.city && match.data?.name && countrySlug) {
        // Определяем падеж из плейсхолдера для склонения текста ссылки
        const { caseType } = parsePlaceholderCase(match.placeholder)
        // Для русской версии применяем склонение к тексту ссылки
        displayText = language === 'ru' ? declineCityName(match.data.name, caseType) : match.data.name

        const existingMarkDef = markDefs.find(md =>
          md._type === 'cityLink' && md.reference?._id === match.data.city._id
        )
        if (existingMarkDef) {
          markKey = existingMarkDef._key
        } else {
          markKey = generateMarkKey()
          markDefs.push({
            _key: markKey,
            _type: 'cityLink',
            reference: {
              _id: match.data.city._id,
              slug: { current: match.data.city.slug },
              name: match.data.city.name,
              countrySlug: match.data.city.countrySlug || countrySlug,
              country: {
                slug: { current: match.data.city.countrySlug || countrySlug }
              }
            }
          })
        }
      } else if (match.type === 'poi' && match.data?.poi && match.data?.name) {
        // Определяем падеж из плейсхолдера для склонения текста ссылки
        const { caseType } = parsePlaceholderCase(match.placeholder)
        // Для русской версии применяем склонение к тексту ссылки
        displayText = language === 'ru' ? declineName(match.data.name, caseType) : match.data.name

        const existingMarkDef = markDefs.find(md =>
          md._type === 'poiLink' && md.reference?._id === match.data.poi._id
        )
        if (existingMarkDef) {
          markKey = existingMarkDef._key
        } else {
          markKey = generateMarkKey()
          markDefs.push({
            _key: markKey,
            _type: 'poiLink',
            reference: {
              _id: match.data.poi._id,
              slug: { current: match.data.poi.slug },
              nameEn: match.data.poi.nameEn,
              countrySlug: match.data.poi.countrySlug,
              citySlug: match.data.poi.citySlug,
              city: {
                slug: { current: match.data.poi.citySlug }
              }
            }
          })
        }
      } else if (match.type === 'service' && match.data?.service && match.data?.name && countrySlug) {
        displayText = match.data.name
        const existingMarkDef = markDefs.find(md =>
          md._type === 'serviceLink' && md.reference?._id === match.data.service._id
        )
        if (existingMarkDef) {
          markKey = existingMarkDef._key
        } else {
          markKey = generateMarkKey()
          markDefs.push({
            _key: markKey,
            _type: 'serviceLink',
            reference: {
              _id: match.data.service._id,
              code: match.data.service.code,
              name: match.data.service.name
            }
          })
        }
      } else if (match.type === 'airport' && match.data?.airport && match.data?.cityName && countrySlug) {
        // Определяем падеж из плейсхолдера
        const { caseType } = parsePlaceholderCase(match.placeholder)
        // Формируем локализованное название аэропорта с учетом падежа
        // Русский: "Аэропорт Цюриха", "Аэропорта Цюриха" и т.д.
        // Английский: "Zurich Airport"
        displayText = formatAirportName(match.data.cityName, caseType, language)

        const existingMarkDef = markDefs.find(md =>
          md._type === 'airportLink' && md.reference?.iata === match.data.airport.iata
        )
        if (existingMarkDef) {
          markKey = existingMarkDef._key
        } else {
          markKey = generateMarkKey()
          markDefs.push({
            _key: markKey,
            _type: 'airportLink',
            reference: {
              iata: match.data.airport.iata,
              slug: match.data.airport.slug,
              countrySlug: match.data.airport.countrySlug || countrySlug
            }
          })
        }
      } else if (match.type === 'post' && match.data?.post && match.data?.name) {
        displayText = match.data.name
        const existingMarkDef = markDefs.find(md =>
          md._type === 'internalLink' && md.reference?._id === match.data.post._id
        )
        if (existingMarkDef) {
          markKey = existingMarkDef._key
        } else {
          markKey = generateMarkKey()
          markDefs.push({
            _key: markKey,
            _type: 'internalLink',
            reference: {
              _id: match.data.post._id,
              slug: { current: match.data.post.slug },
              titleRu: match.data.post.titleRu,
              titleEn: match.data.post.titleEn
            }
          })
        }
      } else if (process.env.NODE_ENV === 'development') {
        // Логируем случаи, когда не удалось создать ссылку
        if (match.type === 'city' && (!match.data?.city || !match.data?.name || !countrySlug)) {
          console.warn('⚠️ Не удалось создать cityLink:', {
            placeholder: match.placeholder,
            hasCity: !!match.data?.city,
            hasName: !!match.data?.name,
            hasCountrySlug: !!countrySlug,
            data: match.data
          })
        } else if (match.type === 'poi' && (!match.data?.poi || !match.data?.name)) {
          console.warn('⚠️ Не удалось создать poiLink:', {
            placeholder: match.placeholder,
            hasPoi: !!match.data?.poi,
            hasName: !!match.data?.name,
            data: match.data
          })
        } else if (match.type === 'service' && (!match.data?.service || !match.data?.name || !countrySlug)) {
          console.warn('⚠️ Не удалось создать serviceLink:', {
            placeholder: match.placeholder,
            hasService: !!match.data?.service,
            hasName: !!match.data?.name,
            hasCountrySlug: !!countrySlug,
            data: match.data
          })
        } else if (match.type === 'post' && (!match.data?.post || !match.data?.name)) {
          console.warn('⚠️ Не удалось создать internalLink для поста:', {
            placeholder: match.placeholder,
            hasPost: !!match.data?.post,
            hasName: !!match.data?.name,
            data: match.data
          })
        } else if (match.type === 'airport' && (!match.data?.airport || !match.data?.cityName || !countrySlug)) {
          console.warn('⚠️ Не удалось создать airportLink:', {
            placeholder: match.placeholder,
            hasAirport: !!match.data?.airport,
            hasCityName: !!match.data?.cityName,
            hasCountrySlug: !!countrySlug,
            data: match.data
          })
        } else if (match.isCurlyBraceFormat && !markKey) {
          // Логируем случаи, когда новый формат не обработан
          console.warn('⚠️ Не удалось создать ссылку для нового формата:', {
            placeholder: match.placeholder,
            displayText: match.displayText,
            type: match.type,
            hasData: !!match.data,
            data: match.data
          })
        }
      }

      // Создаем span с mark или без него
      if (markKey && displayText) {
        processedChildren.push({
          ...child,
          _key: generateMarkKey(),
          text: displayText,
          marks: [...existingMarks, markKey]
        })
      } else {
        // Если не удалось создать ссылку
        if (match.isCurlyBraceFormat && match.displayText) {
          // Для нового формата используем текст из фигурных скобок, даже если ссылка не создана
          processedChildren.push({
            ...child,
            _key: child._key || generateMarkKey(),
            text: match.displayText,
            marks: existingMarks
          })
        } else {
          // Для старого формата пытаемся заменить плейсхолдер на текст
          // ВАЖНО: если заменить не получилось, оставляем плейсхолдер как есть.
          const replacedText = replacePlaceholders(match.placeholder, gptData)
          processedChildren.push({
            ...child,
            _key: child._key || generateMarkKey(),
            text: replacedText || match.placeholder,
            marks: existingMarks
          })
        }
      }

      lastIndex = match.index + match.length
    })

    // Текст после последнего плейсхолдера
    if (lastIndex < text.length) {
      const afterText = text.substring(lastIndex)
      if (afterText) {
        const replacedAfter = replacePlaceholders(afterText, gptData)
        processedChildren.push({
          ...child,
          _key: child._key || generateMarkKey(),
          text: replacedAfter,
          marks: existingMarks
        })
      }
    }
  })

  const result = {
    ...block,
    children: processedChildren,
    markDefs: markDefs.length > 0 ? markDefs : block.markDefs
  }

  // Логирование созданных markDefs убрано для уменьшения шума в консоли

  return result
}

/**
 * Рекурсивно заменяет плейсхолдеры во всех текстовых полях объекта
 * @param {any} obj - Объект, массив или примитив
 * @param {Object} gptData - Объект с мапами плейсхолдеров
 * @returns {any} Объект с замененными плейсхолдерами
 */
export function replacePlaceholdersDeep(obj, gptData) {
  if (!obj || !gptData) {
    return obj
  }

  if (typeof obj === 'string') {
    // Для простых строк заменяем все плейсхолдеры (включая провайдеров как текст)
    // В PortableText блоках провайдеры обрабатываются отдельно с созданием ссылок
    return replacePlaceholders(obj, gptData)
  }

  if (Array.isArray(obj)) {
    return obj.flatMap(item => {
      // Проверяем: блок-параграф с единственным child = {[PHOTO_xxx]} → заменяем на фото-блок
      const photoMatch = getPhotoPlaceholderFromBlock(item, gptData)
      if (photoMatch) {
        return [{
          _type: 'photoAssetReference',
          _key: item._key || generateMarkKey(),
          photoAsset: photoMatch.photoAsset
        }]
      }
      return [replacePlaceholdersDeep(item, gptData)]
    })
  }

  if (typeof obj === 'object') {
    // Table cells are rendered by SanityTable, which needs the original
    // {label[PLACEHOLDER]} token to turn it into an actual link. Treating a
    // simpleTable as a generic object used to strip that token here and leave
    // the renderer with plain text only.
    if (obj._type === 'simpleTable') {
      const table = {}
      for (const key in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, key)) continue
        // Preserve both current and legacy row collections; titles, headers
        // and captions can still use the ordinary text replacement behavior.
        table[key] = /^rows(?:Ru|En)?$/.test(key)
          ? obj[key]
          : replacePlaceholdersDeep(obj[key], gptData)
      }
      return table
    }

    // Для PortableText блоков с children обрабатываем специально (создаем ссылки для провайдеров)
    if (obj.children && Array.isArray(obj.children)) {
      return softenBlock(processPortableTextBlock(obj, gptData))
    }

    // Для других объектов обрабатываем все поля рекурсивно
    const result = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = replacePlaceholdersDeep(obj[key], gptData)
      }
    }
    return result
  }

  return obj
}

/**
 * Экранирует специальные символы для использования в регулярных выражениях
 * @param {string} str - Строка для экранирования
 * @returns {string} Экранированная строка
 */
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
